import SearchInput from 'components/SearchInput';
import ListTabs from 'components/ListTabs';
import ListWrapper from 'components/ListWrapper';
import Typography from 'components/Typography';
import ListActionsWrapper from 'components/ListActionsWrapper';
import DataTable from 'components/DataTable';
import {Address, DataItem, ScriptStatus} from 'types';
import styles from './Requests.module.scss';
import stat_time from '../DeliveryPlanner/icons/stat_time.svg';
import calendar from '../DeliveryPlanner/icons/calendar.svg';
import useRequests from './useRequests';
import Pagination from 'components/Pagination';
import DateTimePicker from 'components/DateTimePicker';
import Select from 'components/Select';
import {components} from 'react-select';
import {get, groupBy, isArray, map, reject, sortBy as sortLodash} from 'lodash';
import nyPostalCodeBoroughOptions from 'shared/ny-postal-code-borough-options';
import locationIcon from './location.svg';
import Button from 'components/Button';
import Modal from 'components/Modal';
import {NewCustomer} from 'pages/Settings/Users/Users';
import {CreateAddress} from 'pages/UserForm/UserAddresses/UserAddresses';
import {NewProduct} from 'pages/Products/Products';
import {addressToString} from 'helpers/address';
import classNames from 'classnames';
import FilterIcon from './FilterIcon';
import ScriptStatusSelect from 'components/ScriptStatusSelect';
import {leadStatuses} from 'pages/RequestDetails/RequestDetails';
import ReactSwitch from 'react-switch';

// Group NYC postal codes by borough for location filtering
const locationOptionsGrouped = groupBy(nyPostalCodeBoroughOptions, 'label');
const sorted: {label: string; value: any}[] = map(
  locationOptionsGrouped,
  (obj, key) => ({
    label: key,
    value: map(obj, 'value'),
  })
);

// Sort location options alphabetically for better UX
const locationOptions = sortLodash(sorted, 'label');

// Status filter options for completed requests
const statusesOptions = [
  {
    label: 'Statuses',
    options: [
      {
        label: 'Cancel script',
        value: 'cancel_script',
      },
      {
        label: 'No script available',
        value: 'no_script_available',
      },
      {
        label: 'Completed',
        value: 'completed',
      },
    ],
  },
];

// Request type options for app requests
const typesOptions = [
  {
    label: 'Call doctor',
    value: 'call_doctor',
  },
  {
    label: 'Meds kit',
    value: 'meds_kit',
  },
  {
    label: 'App refill',
    value: 'app_refill',
  },
  {
    label: 'Landing page',
    value: 'landing_page',
  },
  {
    label: 'Meta ads',
    value: 'meta_ads',
  },
];

// Payment status options for micromerchant orders
const paidOptions = [
  {label: 'Paid', value: 'true'},
  {label: 'Unpaid', value: 'false'},
];

type Props = {
  source: string; // 'app' for app requests, 'micromerchant' for orders
};

function Requests({source}: Props) {
  // Custom hook that manages all the state and logic for requests/orders
  const {
    SCHEMA,
    MM_SCHEMA,
    MMTable,
    onChangeTab,
    getClients,
    getProducts,
    getAddress,
    clients,
    products,
    addresses,
    setClients,
    setProducts,
    setAddresses,
    filter,
    setFilter,
    filterVisible,
    setFilterVisible,
    filtered,
    resetFilter,
    applyFilter,
    currentPage,
    setCurrentPage,
    data,
    totalPages,
    dailyCount,
    loading,
    sortBy,
    sortOrder,
    tabs,
    status,
    onSort,
    setStatus,
    keyWord,
    MMtabs,
    location,
    preferenceTime,
    paid,
    onChangeStatuses,
    statuses,
    visible,
    setVisible,
    pageSize,
    setPageSize,
    count,
    onSubmit,
    submitting,
    handleNavigateRequest,
    onSearch,
    productVisible,
    setProductVisible,
    addressVisible,
    setAddressVisible,
    isModalOpen,
    setModalOpen,
    productIds,
    setProductIds,
    userId,
    setUserId,
    addressId,
    setAddressId,
    history,
    selectedOrders,
    hasOtc,
    showStatusModal,
    setShowStatusModal,
    orderStatus,
    setOrderStatus,
    statusesData,
    setStatusesData,
    submitOrderStatutes,
    setIsUrgentDelivery,
    isUrgentDelivery,
  } = useRequests({source});

  return (
    <>
      {/* Header section with search and action buttons */}
      <div className={styles.top_header}>
        <SearchInput
          className={styles.input}
          value={keyWord}
          onChange={(val) => {
            onSearch(val.target.value);
          }}
          placeholder="Search"
        />
        <div className={styles.headerRight}>
          {/* Show bulk update button when orders are selected */}
          {selectedOrders.length > 0 && (
            <Button onClick={() => setShowStatusModal(true)} color="blue">
              Bulk Update
            </Button>
          )}
          {/* Add new request/order button - hidden for app requests */}
          {source !== 'app' && (
            <Button
              onClick={() => {
                setVisible(true);
                getClients(1, '');
                getProducts(1, '');
              }}
              color="blue"
            >
              {source === 'app' ? 'Add request' : 'Add Order'}
            </Button>
          )}
        </div>
      </div>

      {/* Daily count display */}
      <div style={{marginTop: 10}} className={styles.top_header}>
        <Typography variant="h4">
          {/* {source !== 'app' && `Avarage Processing Time: ${processingTime}`} */}
        </Typography>
        <Typography variant="h4">
          {source === 'app' ? 'Today’s App Requests' : 'Today’s Orders :'}{' '}
          {dailyCount}
        </Typography>
      </div>

      <ListWrapper>
        {/* Tab navigation for different status categories */}
        <ListTabs
          onChange={(val) => {
            setStatus(val);
            setCurrentPage(1); // Reset to first page when changing tabs
          }}
          isSupportPage={false}
          activeTabValue={status}
          tabs={source === 'micromerchant' ? MMtabs : tabs}
          hasOtc={hasOtc}
        />

        <ListActionsWrapper>
          <div className={styles.row}>
            {/* Filter button with visual indicator when filters are applied */}
            <div
              className={styles.filterBtn}
              style={{
                borderColor: filtered ? '#0072b3' : '#E0E0E0',
              }}
              onClick={() => setFilterVisible(true)}
            >
              <FilterIcon isFilled={filtered} />
              <div
                style={{
                  color: '#28303F',
                }}
              >
                {filtered ? 'Filtered' : 'Filter'}
              </div>
            </div>

            {/* Status filter for completed app requests */}
            {source === 'app' && status === 'completed' && (
              <Select
                className={classNames(
                  'DeliveryPlanner__Filter',
                  styles.selectCon,
                  styles.selectStatus
                )}
                placeholder="Status"
                components={{
                  Input: () => null,
                  DropdownIndicator: (props) =>
                    !(statuses.length > 0) ? (
                      <components.DropdownIndicator {...props} />
                    ) : null,
                  // Custom display for multiple selected values
                  SingleValue: ({children, ...props}) => {
                    return (
                      <components.SingleValue {...props}>
                        {isArray(props.selectProps.value)
                          ? props.selectProps.value
                              .map((item: {label: string}) => item.label)
                              .join(', ')
                          : ''}
                      </components.SingleValue>
                    );
                  },
                  IndicatorSeparator: () => null,
                }}
                isSearchable={false}
                onChange={(data) => {
                  history.push('/requests/page/1');
                  onChangeStatuses(data);
                }}
                isClearable
                options={statusesOptions}
                value={statuses}
              />
            )}
          </div>

          {/* Column display selector for micromerchant orders */}
          {source === 'micromerchant' && (
            <div className={styles.row}>
              <Select
                className={'Table_Select'}
                styles={{
                  container: (base) => ({
                    ...base,
                    marginLeft: 0,
                  }),
                }}
                isSearchable={false}
                onChange={onChangeTab}
                options={[
                  {
                    label: 'Table column',
                    options: MM_SCHEMA.slice(1).map((item) => ({
                      ...item,
                      value: item.dataKey,
                      label: item.header,
                    })),
                  },
                ]}
                value={MMTable}
                placeholder={
                  <span className="select-placeholder-text">Display</span>
                }
                components={{
                  Input: () => null,
                  DropdownIndicator: (props) =>
                    !preferenceTime ? (
                      <components.DropdownIndicator {...props} />
                    ) : null,
                  SingleValue: ({children, ...props}) => {
                    return (
                      <components.SingleValue {...props}>
                        Display
                      </components.SingleValue>
                    );
                  },
                  IndicatorSeparator: () => null,
                }}
                closeMenuOnSelect={false}
              />
            </div>
          )}
        </ListActionsWrapper>

        {/* Main data table with sorting and navigation */}
        <DataTable
          schema={source === 'app' ? SCHEMA : MMTable}
          data={data as DataItem[]}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onClick={handleNavigateRequest}
          onSort={onSort}
          loading={loading}
        />

        {/* Pagination controls */}
        {!loading && (
          <Pagination
            count={count}
            basePath={source === 'app' ? '/requests' : '/orders'}
            page={currentPage}
            total={totalPages}
            pageSize={pageSize}
            setCurrentPage={setCurrentPage}
            setPageSize={setPageSize}
          />
        )}
      </ListWrapper>

      {/* Filter Modal */}
      <Modal
        isOpen={filterVisible}
        className={styles.filterModal}
        onClose={() => setFilterVisible(false)}
        onRequestClose={() => setFilterVisible(false)}
      >
        <Typography variant="h2">Filter</Typography>
        <div className={styles.filterContainer}>
          {/* Location and Redispatch filters */}
          <div className={styles.row_inputs}>
            <div style={{flex: 1}}>
              {/* Location filter with borough grouping */}
              <Select
                className={'DeliveryPlanner__Filter'}
                placeholder="Location"
                menuPosition="fixed"
                components={{
                  Input: () => null,
                  DropdownIndicator: (props) =>
                    !location ? (
                      <components.DropdownIndicator {...props} />
                    ) : null,
                  Control: ({children, ...props}) => {
                    return (
                      <components.Control {...props}>
                        <img
                          className={styles.locationIcon}
                          src={locationIcon}
                          alt="location_icon"
                        />
                        {children}
                      </components.Control>
                    );
                  },
                  // Handle multiple location selections
                  SingleValue: ({children, ...props}) => {
                    return (
                      <components.SingleValue {...props}>
                        {isArray(props.selectProps.value)
                          ? props.selectProps.value
                              .map((item: {label: string}) => item.label)
                              .join(', ')
                          : ''}
                      </components.SingleValue>
                    );
                  },
                  IndicatorSeparator: () => null,
                }}
                isSearchable={false}
                onChange={(data) => {
                  // Toggle location selection - add if not present, remove if present
                  let location: any = [];
                  if (data) {
                    location = filter.location.some(
                      (item: any) => item.label === data.label
                    )
                      ? reject(filter.location, data)
                      : [...filter.location, data];
                  }
                  setFilter((prev: any) => ({
                    ...prev,
                    location,
                  }));
                }}
                isClearable
                options={[
                  {
                    label: 'Locations',
                    options: locationOptions,
                  },
                ]}
                defaultValue={filter.location}
                value={filter.location}
              />
            </div>

            {/* Attempt filter for non-app requests (1-4 attempts, 5+ for unlimited) */}
            {source !== 'app' && (
              <Select
                className={classNames(styles.selectCon)}
                placeholder="Redispatch Attempt"
                isClearable
                components={{
                  Input: () => null,
                  DropdownIndicator: (props) =>
                    !filter.redispatch ? (
                      <components.DropdownIndicator {...props} />
                    ) : null,
                  IndicatorSeparator: () => null,
                }}
                isSearchable={false}
                onChange={(data) => {
                  setFilter((prev: any) => ({
                    ...prev,
                    redispatch: data || undefined,
                  }));
                }}
                options={[
                  {
                    label: '1',
                    value: '1',
                  },
                  {
                    label: '2',
                    value: '2',
                  },
                  {
                    label: '3',
                    value: '3',
                  },
                  {
                    label: '4',
                    value: '4',
                  },
                  {
                    label: '5+',
                    value: '5',
                  },
                ]}
                value={filter.redispatch}
              />
            )}
          </div>

          {/* Type and Date filters */}
          <div className={styles.row_inputs}>
            {/* Request type filter for app requests */}
            {source === 'app' && (
              <Select
                className={classNames(styles.selectCon)}
                placeholder="Type"
                isClearable
                components={{
                  Input: () => null,
                  DropdownIndicator: (props) =>
                    !filter.types ? (
                      <components.DropdownIndicator {...props} />
                    ) : null,
                  IndicatorSeparator: () => null,
                }}
                isSearchable={false}
                onChange={(data) => {
                  // Clear prescription and meta_event_type when changing away from meta_ads/landing_page
                  if (
                    data?.value !== 'meta_ads' &&
                    data?.value !== 'landing_page'
                  ) {
                    setFilter((prev: any) => ({
                      ...prev,
                      prescription: undefined,
                      types: data || undefined,
                      meta_event_type: undefined,
                    }));
                  }
                  setFilter((prev: any) => ({
                    ...prev,
                    types: data || undefined,
                  }));
                }}
                options={typesOptions}
                value={filter.types}
              />
            )}

            {/* App-specific filters */}
            {source === 'app' && (
              <>
                {/* Prescription filter - only for meta_ads and landing_page */}
                {(filter?.types?.value === 'meta_ads' ||
                  filter?.types?.value === 'landing_page') && (
                  <Select
                    className={classNames(styles.selectCon)}
                    placeholder="Prescription"
                    isClearable
                    components={{
                      Input: () => null,
                      DropdownIndicator: (props) =>
                        !filter.prescription ? (
                          <components.DropdownIndicator {...props} />
                        ) : null,
                      IndicatorSeparator: () => null,
                    }}
                    isSearchable={false}
                    onChange={(data) => {
                      setFilter((prev: any) => ({
                        ...prev,
                        prescription: data || undefined,
                      }));
                    }}
                    options={[
                      {
                        label: 'Have prescription',
                        value: 'True',
                      },
                      {
                        label: 'No prescription',
                        value: 'False',
                      },
                    ]}
                    value={filter.prescription}
                  />
                )}

                {/* Created date filter with dynamic width based on type selection */}
                <div
                  style={{
                    width:
                      filter?.types?.value === 'meta_ads' ||
                      filter?.types?.value === 'landing_page'
                        ? '100%'
                        : '295px',
                  }}
                  className={
                    filter?.types?.value === 'meta_ads' ||
                    filter?.types?.value === 'landing_page'
                      ? ''
                      : styles.selectCon
                  }
                >
                  {/* @ts-ignore */}
                  <DateTimePicker
                    icon={calendar}
                    selectsRange={true}
                    showTimeSelect={false}
                    dateFormat="MM/dd/yy E"
                    startDate={filter.createdDate?.start}
                    endDate={filter.createdDate?.end}
                    placeholder="Created Date"
                    className={styles.customInput}
                    onChange={(data) => {
                      setFilter((prev: any) => ({
                        ...prev,
                        createdDate: {
                          start: data[0] || undefined,
                          end: data[1] || undefined,
                        },
                      }));
                    }}
                  />
                </div>
              </>
            )}

            {/* Micromerchant-specific filters */}
            {source !== 'app' && (
              <>
                {/* Order type filter */}
                <Select
                  className={styles.selectCon}
                  isSearchable={false}
                  menuPosition="fixed"
                  onChange={(val: any) => {
                    setFilter((prev: any) => ({
                      ...prev,
                      onlyOTC: val || undefined,
                    }));
                  }}
                  options={[
                    {label: 'Rx', value: 'false'},
                    {label: 'Rx Refill ', value: 'mm_refill'},
                    {label: 'OTC', value: 'true'},
                    {label: 'Meds Kit', value: 'meds_kit'},
                  ]}
                  value={filter.onlyOTC}
                  placeholder={
                    <span className="select-placeholder-text">Type</span>
                  }
                  isClearable
                  components={{
                    Input: () => null,
                    DropdownIndicator: (props) =>
                      !filter.onlyOTC ? (
                        <components.DropdownIndicator {...props} />
                      ) : null,
                    IndicatorSeparator: () => null,
                  }}
                />

                {/* Payment status filter */}
                <Select
                  className={styles.selectCon}
                  isSearchable={false}
                  menuPosition="fixed"
                  onChange={(val: any) => {
                    setFilter((prev: any) => ({
                      ...prev,
                      paid: val || undefined,
                    }));
                  }}
                  options={paidOptions}
                  value={filter.paid}
                  placeholder={
                    <span className="select-placeholder-text">Paid/Unpaid</span>
                  }
                  isClearable
                  components={{
                    Input: () => null,
                    DropdownIndicator: (props) =>
                      !paid ? (
                        <components.DropdownIndicator {...props} />
                      ) : null,
                    IndicatorSeparator: () => null,
                  }}
                />
              </>
            )}
          </div>

          {/* Meta ads lead status filter */}
          {filter.types?.value === 'meta_ads' && (
            <Select
              onChange={(val) => {
                setFilter((prev: any) => ({
                  ...prev,
                  meta_event_type: val?.value,
                }));
              }}
              isSearchable={false}
              components={{
                DropdownIndicator: (props) =>
                  !filter.meta_event_type ? (
                    <components.DropdownIndicator {...props} />
                  ) : null,
                IndicatorSeparator: () => null,
              }}
              isClearable
              placeholder="Select lead status"
              options={Object.entries(leadStatuses).map(([key, value]) => ({
                value: key,
                label: value,
              }))}
              value={
                get(filter, 'meta_event_type')
                  ? {
                      value: String(get(filter, 'meta_event_type', '')),
                      label:
                        leadStatuses[
                          get(
                            filter,
                            'meta_event_type',
                            ''
                          ) as keyof typeof leadStatuses
                        ],
                    }
                  : undefined
              }
            />
          )}

          {/* Additional date filters for micromerchant */}
          {source !== 'app' && (
            <>
              <div className={styles.row}>
                {/* Bill date range filter */}
                {/* @ts-ignore */}
                <DateTimePicker
                  icon={calendar}
                  selectsRange={true}
                  showTimeSelect={false}
                  dateFormat="MM/dd/yy E"
                  startDate={filter.billDate?.start}
                  endDate={filter.billDate?.end}
                  placeholder="Bill Date"
                  className={styles.customInput}
                  onChange={(data) => {
                    setFilter((prev: any) => ({
                      ...prev,
                      billDate: {
                        start: data[0] || undefined,
                        end: data[1] || undefined,
                      },
                    }));
                  }}
                />

                {/* Created date range filter */}
                {/* @ts-ignore */}
                <DateTimePicker
                  icon={calendar}
                  selectsRange={true}
                  showTimeSelect={false}
                  dateFormat="MM/dd/yy E"
                  startDate={filter.createdDate?.start}
                  endDate={filter.createdDate?.end}
                  placeholder="Created Date"
                  className={styles.customInput}
                  onChange={(data) => {
                    setFilter((prev: any) => ({
                      ...prev,
                      createdDate: {
                        start: data[0] || undefined,
                        end: data[1] || undefined,
                      },
                    }));
                  }}
                />
              </div>

              <div className={styles.row}>
                {/* Preference date filter */}
                <DateTimePicker
                  icon={calendar}
                  showTimeSelect={false}
                  dateFormat="MM/dd/yy E"
                  value={filter.date}
                  placeholder="Pr. Date"
                  className={styles.customInput}
                  onChange={(data) => {
                    setFilter((prev: any) => ({
                      ...prev,
                      date: data,
                    }));
                  }}
                />

                {/* Preference time filter */}
                <Select
                  className={styles.selectCon}
                  isSearchable={false}
                  menuPosition="fixed"
                  onChange={(val: any) => {
                    setFilter((prev: any) => ({
                      ...prev,
                      preferenceTime: val,
                    }));
                  }}
                  options={[
                    {label: 'Morning', value: 'morning'},
                    {label: 'Afternoon', value: 'afternoon'},
                    {label: 'Evening', value: 'evening'},
                  ]}
                  value={filter.preferenceTime}
                  placeholder={
                    <span className="select-placeholder-text">Pr. Time</span>
                  }
                  isClearable
                  components={{
                    Input: () => null,
                    DropdownIndicator: (props) =>
                      !preferenceTime ? (
                        <components.DropdownIndicator {...props} />
                      ) : null,
                    Control: ({children, ...props}) => (
                      <components.Control {...props} className="select-row">
                        <img className={'timer'} src={stat_time} alt="timer" />
                        {children}
                      </components.Control>
                    ),

                    IndicatorSeparator: () => null,
                  }}
                />
              </div>
              <div className={styles.row}>
                <Select
                  className={styles.selectCon}
                  isSearchable={false}
                  menuPosition="fixed"
                  onChange={(val: any) => {
                    setFilter((prev: any) => ({
                      ...prev,
                      deliveredBy: val,
                    }));
                  }}
                  options={[
                    {label: 'Meds', value: 'meds'},
                    {label: 'Delivery Partner', value: 'delivery_partner'},
                  ]}
                  value={filter.deliveredBy}
                  placeholder={
                    <span className="select-placeholder-text">
                      Delivered By
                    </span>
                  }
                  isClearable
                />
                <Select
                  className={styles.selectCon}
                  isSearchable={false}
                  menuPosition="fixed"
                  onChange={(val: any) => {
                    setFilter((prev: any) => ({
                      ...prev,
                      isUrgentFilter: val,
                    }));
                  }}
                  options={[
                    {label: 'Yes', value: 'true'},
                    {label: 'No', value: 'false'},
                  ]}
                  value={filter.isUrgentFilter}
                  placeholder={
                    <span className="select-placeholder-text">Flag</span>
                  }
                  isClearable
                />
              </div>
              <DateTimePicker
                icon={calendar}
                showTimeSelect={false}
                dateFormat="MM/dd/yy E"
                value={filter.statusDate}
                placeholder="Status Date"
                className={classNames(styles.customInput, styles.selectCon)}
                onChange={(data) => {
                  setFilter((prev: any) => ({
                    ...prev,
                    statusDate: data,
                  }));
                }}
              />
            </>
          )}
        </div>

        {/* Filter action buttons */}
        <div className={styles.filterBtnContainer}>
          <Button onClick={resetFilter} color="gray">
            Reset
          </Button>
          <Button onClick={applyFilter} color="blue">
            Apply
          </Button>
        </div>
      </Modal>

      {/* Add New Request/Order Modal */}
      <Modal
        isOpen={visible}
        onClose={() => setVisible(false)}
        className={styles.list_wrapper}
        onRequestClose={() => setVisible(false)}
      >
        <Typography variant="h2">
          Add new {source === 'app' ? 'request' : 'order'}
        </Typography>

        {/* Patient selection with add new option */}
        <div
          style={{display: 'flex', alignItems: 'center', gap: 10}}
          className={styles.label}
        >
          <div>Patient </div>
          <PlusComponent onClick={() => setModalOpen(true)} />
        </div>
        <Select
          onInputChange={(text, action) => {
            if (action.action === 'input-change') {
              getClients(1, text); // Search clients as user types
            }
          }}
          onMenuScrollToBottom={(event) => {
            getClients(Math.floor(clients.length / 50) + 1, ''); // Load more clients on scroll
          }}
          menuPosition="fixed"
          placeholder="Click to select"
          onChange={(item) => {
            setUserId(item?.value);
            getAddress(Number(item?.value)); // Load addresses for selected patient
          }}
          value={
            clients.find((item) => item.id === userId)
              ? {
                  value:
                    clients.find((item) => item.id === userId)?.user?.id || '',
                  label:
                    clients.find((item) => item.id === userId)?.user
                      ?.first_name +
                    ' ' +
                    clients.find((item) => item.id === userId)?.user?.last_name,
                }
              : undefined
          }
          options={clients.map((item) => ({
            value: item.id,
            label: item.user.first_name + ' ' + item.user.last_name,
          }))}
        />

        {/* Address selection - only shown after patient is selected */}
        {userId !== 0 && (
          <>
            <div
              style={{display: 'flex', alignItems: 'center', gap: 10}}
              className={styles.label}
            >
              <div>Address</div>
              <PlusComponent onClick={() => setAddressVisible(true)} />
            </div>
            <Select
              placeholder="Click to select"
              menuPosition="fixed"
              onChange={(item) => {
                setAddressId(item?.value);
              }}
              value={
                addresses.find((item) => item.id === addressId)
                  ? {
                      value:
                        addresses.find((item) => item.id === addressId)?.id ||
                        '',
                      label: addressToString(
                        addresses.find(
                          (item) => item.id === addressId
                        ) as Address
                      ),
                    }
                  : undefined
              }
              options={addresses.map((item) => ({
                value: item.id,
                label:
                  item.address_line_1 +
                  ',' +
                  item.city +
                  ',' +
                  item.state +
                  ',' +
                  item.zipcode,
              }))}
            />
          </>
        )}

        {/* Product selection with search and pagination */}
        <div
          style={{display: 'flex', alignItems: 'center', gap: 10}}
          className={styles.label}
        >
          <div>Product</div>
          <PlusComponent onClick={() => setProductVisible(true)} />
        </div>
        <Select
          onInputChange={(text, action) => {
            if (action.action === 'input-change') {
              getProducts(1, text); // Search products as user types
            }
          }}
          onMenuScrollToBottom={(event) => {
            getProducts(Math.floor(products.length / 50) + 1, ''); // Load more products on scroll
          }}
          placeholder="Click to select"
          menuPosition="fixed"
          onChange={(item) => {
            if (isArray(item)) {
              setProductIds(item.map((item) => item.value));
            } else {
              setProductIds([item?.value]);
            }
          }}
          value={
            productIds.length > 0
              ? {
                  value:
                    products.find((item) => item.id === productIds[0])?.id ||
                    '',
                  label: products.find((item) => item.id === productIds[0])
                    ?.name,
                }
              : undefined
          }
          //@ts-ignore
          isSearchable={true}
          //@ts-ignore
          isMulti={source === 'app'} // Multi-select only for app requests
          options={products.map((item) => ({
            value: item.id,
            label: item.name,
          }))}
        />

        {/* Submit button - disabled until all required fields are filled */}
        <Button
          loading={submitting}
          disabled={!addressId || !userId || !productIds.length}
          className={styles.addBtn}
          onClick={onSubmit}
          color="blue"
        >
          {source === 'app' ? 'Add request' : 'Add Order'}
        </Button>

        {/* Nested modals for creating new entities */}
        <NewCustomer
          onSuccess={(data) => {
            setClients((pr) => [data, ...pr]);
            setUserId(data.id);
          }}
          isOpen={isModalOpen}
          onClose={() => setModalOpen(false)}
        />

        <CreateAddress
          customer_id={userId}
          handleClose={() => setAddressVisible(false)}
          onSuccess={(data) => {
            setAddresses((pr) => [data, ...pr]);
            setAddressId(data.id);
          }}
          isOpen={addressVisible}
        />

        <NewProduct
          visible={productVisible}
          onClose={() => setProductVisible(false)}
          onSuccess={(data) => {
            setProducts((pr) => [data, ...pr]);
            setProductIds([data.id]);
          }}
        />
      </Modal>

      {/* Bulk Status Update Modal */}
      <Modal
        isOpen={showStatusModal}
        onClose={() => setShowStatusModal(false)}
        className={styles.status_wrapper}
      >
        <Typography variant="h2">Bulk Update</Typography>
        <div className={styles.times}>
          {/* Status selection */}
          <div style={{flex: 1}}>
            <div className={styles.label}>Status:</div>
            <ScriptStatusSelect
              name={`status`}
              status={orderStatus as ScriptStatus}
              onChange={(val) => setOrderStatus(val?.value || '')}
              type="prescription"
              origin={source}
              isPvfr={true}
              menuPosition="fixed"
              classNamePrefix={'order_select'}
              className={orderStatus || 'default_selected'}
            />
          </div>

          {/* Preference date selection */}
          <div style={{flex: 1}}>
            <div className={styles.label}>Preference date:</div>
            <div className={styles.calendarRoot}>
              <DateTimePicker
                showTimeSelect={false}
                dateFormat="MM/dd/yy E"
                value={
                  statusesData.preference_date
                    ? statusesData.preference_date
                    : undefined
                }
                onChange={(date) => {
                  setStatusesData((pr) => ({
                    ...pr,
                    preference_date: date ? date : null,
                  }));
                }}
              />
            </div>
          </div>
        </div>
        <div className={styles.times}>
          {/* Preference time selection */}
          <div style={{flex: 1}}>
            <div className={styles.label}>Preference time:</div>
            <Select
              className={styles.select}
              onChange={(time) => {
                setStatusesData((pr) => ({
                  ...pr,
                  preference_time: String(time?.value || ''),
                }));
              }}
              placeholder={
                <span className="select-placeholder-text">Select time</span>
              }
              isClearable
              components={{
                DropdownIndicator: (props) =>
                  !statusesData.preference_time ? (
                    <components.DropdownIndicator {...props} />
                  ) : null,
              }}
              value={
                statusesData.preference_time
                  ? {
                      value: statusesData.preference_time,
                      label:
                        statusesData.preference_time[0].toUpperCase() +
                        statusesData.preference_time.slice(1),
                    }
                  : null
              }
              options={[
                {value: 'morning', label: 'Morning'},
                {value: 'afternoon', label: 'Afternoon'},
                {value: 'evening', label: 'Evening'},
              ]}
              size="md"
            />
          </div>
          <div
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 30,
              paddingTop: 10,
            }}
            className={styles.times}
          >
            <div className={styles.label}>Flag: </div>
            <div className={styles.label}>
              <ReactSwitch
                onColor="#0072b3"
                onChange={() => {
                  setIsUrgentDelivery(!isUrgentDelivery);
                }}
                handleDiameter={16}
                height={20}
                width={40}
                checkedIcon={false}
                uncheckedIcon={false}
                checked={isUrgentDelivery}
              />
            </div>
          </div>
        </div>

        {/* Submit bulk update */}
        <div className={styles.actions}>
          <Button
            color="green"
            disabled={!orderStatus}
            onClick={submitOrderStatutes}
            size="md"
          >
            Submit
          </Button>
        </div>
      </Modal>
    </>
  );
}

// Reusable plus button component for adding new entities
export const PlusComponent = ({onClick}: {onClick: () => void}) => {
  return (
    <div
      onClick={onClick}
      style={{
        width: 22,
        height: 22,
        display: 'flex',
        backgroundColor: '#27ae60',
        color: '#fff',
        borderRadius: 20,
        justifyContent: 'center',
        alignItems: 'center',
        cursor: 'pointer',
      }}
    >
      +
    </div>
  );
};

export default Requests;
